import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { confirmRegistryReview } from "@/services/patient-service";
import { formatCPF, formatPhone } from "@/utils/formatters";
import { AlertTriangle, CheckCircle2, Edit, Loader2 } from "lucide-react";

interface RegistryReviewGateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patient: {
    id: string;
    name: string;
    cpf: string;
    birth_date?: string;
    cellphone: string;
  } | null;
  onConfirmed: () => void;
  onEdit: () => void;
}

export function RegistryReviewGateDialog({
  open,
  onOpenChange,
  patient,
  onConfirmed,
  onEdit,
}: RegistryReviewGateDialogProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const { toast } = useToast();

  if (!patient) return null;

  const formattedBirth = patient.birth_date
    ? patient.birth_date.split("-").reverse().join("/")
    : "—";

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await confirmRegistryReview(patient.id);
      onConfirmed();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Erro ao confirmar revisão",
        description: error instanceof Error ? error.message : "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const handleEdit = () => {
    onOpenChange(false);
    onEdit();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="border-b pb-2">
          <DialogTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Revisar cadastro do paciente
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <p className="text-xs text-muted-foreground">
            O cadastro deste paciente não foi revisado nos últimos dias. Confirme
            os dados antes de marcar o atendimento como concluído.
          </p>

          <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
            <div className="space-y-0.5">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Nome
              </p>
              <p className="text-xs font-medium">{patient.name}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-0.5">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  CPF
                </p>
                <p className="text-xs">{formatCPF(patient.cpf) || "—"}</p>
              </div>
              <div className="space-y-0.5">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Nascimento
                </p>
                <p className="text-xs">{formattedBirth}</p>
              </div>
              <div className="space-y-0.5 col-span-2">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Telefone
                </p>
                <p className="text-xs">
                  {formatPhone(patient.cellphone) || "—"}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-3 border-t">
          <Button
            variant="outline"
            size="sm"
            className="btn-header"
            onClick={handleEdit}
            disabled={isConfirming}
          >
            <Edit className="h-3 w-3 mr-1.5" />
            Corrigir
          </Button>
          <Button
            size="sm"
            className="btn-header bg-green-600 hover:bg-green-700 text-white"
            onClick={handleConfirm}
            disabled={isConfirming}
          >
            {isConfirming ? (
              <>
                <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                Confirmando...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-3 w-3 mr-1.5" />
                Confirmar tudo certo
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
